import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { TextSectionBlock } from "./blocks/TextSectionBlock";
import { ImageBlock } from "./blocks/ImageBlock";
import { TwoColumnBlock } from "./blocks/TwoColumnBlock";
import { MaterialReferenceBlock } from "./blocks/MaterialReferenceBlock";
import { GalleryBlock } from "./blocks/GalleryBlock";
import { CTABlock } from "./blocks/CTABlock";
import { ImageHotspotBlock } from "./blocks/ImageHotspotBlock";
import { BulletListBlock } from "./blocks/BulletListBlock";
import { MaterialStrategyBlock } from "./blocks/MaterialStrategyBlock";
import { ResultBlock } from "./blocks/ResultBlock";
import { SpecsBlock } from "./blocks/SpecsBlock";
import { ChallengesBlock } from "./blocks/ChallengesBlock";
import { OutcomesBlock } from "./blocks/OutcomesBlock";
import { QuoteBlock } from "./blocks/QuoteBlock";
import { ScenariosBlock } from "./blocks/ScenariosBlock";

interface Block {
  id: string;
  block_type: string;
  content: Record<string, unknown>;
  sort_order: number;
}

interface BlockRendererProps {
  block: Block;
}

function renderBlock(block: Block) {
  switch (block.block_type) {
    case "text_section":
      return <TextSectionBlock content={block.content as any} />;
    case "image":
      return <ImageBlock content={block.content as any} />;
    case "two_column":
      return <TwoColumnBlock content={block.content as any} />;
    case "material_reference":
      return <MaterialReferenceBlock content={block.content as any} />;
    case "gallery":
      return <GalleryBlock content={block.content as any} />;
    case "cta":
      return <CTABlock content={block.content as any} />;
    case "image_hotspot":
      return <ImageHotspotBlock content={block.content as any} />;
    case "bullet_list":
      return <BulletListBlock content={block.content as any} />;
    case "material_strategy":
      return <MaterialStrategyBlock content={block.content as any} />;
    case "result":
      return <ResultBlock content={block.content as any} />;
    case "specs":
      return <SpecsBlock content={block.content as any} />;
    case "challenges":
      return <ChallengesBlock content={block.content as any} />;
    case "outcomes":
      return <OutcomesBlock content={block.content as any} />;
    case "quote":
      return <QuoteBlock content={block.content as any} />;
    case "scenarios":
      return <ScenariosBlock content={block.content as any} />;
    default:
      return null;
  }
}

export function BlockRenderer({ block }: BlockRendererProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const content = renderBlock(block);
  if (!content) return null;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      {content}
    </motion.div>
  );
}
